import { defineComponent as _defineComponent } from 'vue'
import { computed, inject } from 'vue';
import type { TablePanel } from 'table-panel';

import { IconDefinition } from '@icons/core';

import { faPlus } from '@icons/regular/faPlus';
import { faTrashCan } from '@icons/regular/faTrashCan';
import { faSync } from '@icons/regular/faSync';
import { faEllipsisV } from '@icons/regular/faEllipsisV';
import { faExpandAlt } from '@icons/solid/faExpandAlt';
import { faCompressAlt } from '@icons/solid/faCompressAlt';
import { faFilter } from '@icons/regular/faFilter';
import { faInfo } from '@icons/regular/faInfo';

import { TableRefs, DataItem } from './table';
import { useTableMenu } from './utils';

export interface FilterEvent {
  key: string;
  value: boolean;
}

/** `PanelSelectionManager` component emits. */
export interface Emits {
  (event: 'minimize' | 'maximize'): void;
  (event: 'filter', value: FilterEvent): void;
}

// type MenuButton = TablePanel.MenuItem & { type: 'button' };

interface MenuButton {
  key: string;
  type: 'button';
  label: string;
  icon?: IconDefinition;
  disabled: boolean;
  click: () => void;
}

interface MenuSeparator {
  key: string;
  type: 'separator';
}


export default /*#__PURE__*/_defineComponent({
  __name: 'Toolbar',
  emits: ["minimize", "maximize", "filter"],
  setup(__props, { emit }: { emit: ({
  (event: 'minimize' | 'maximize'): void;
  (event: 'filter', value: FilterEvent): void;
}), expose: any, slots: any, attrs: any }) {

/* eslint-disable @typescript-eslint/non-nullable-type-assertion-style */



const path = inject('$path') as TableRefs['path'];
const tableLoading = inject('$tableLoading') as TableRefs['tableLoading'];
const createAction = inject('$createAction') as TableRefs.Action;
const deleteAction = inject('$deleteAction') as TableRefs.Action;
const queryText = inject('$queryText') as TableRefs['queryText'];
const selection = inject('$selection') as TableRefs.Selection;
const fullscreen = inject('$fullscreen') as TableRefs['fullscreen'];
const filterTags = inject('$tableFilterTags') as TableRefs['tableFilterTags'];
const filterParams = inject('$tableFilterParams') as TableRefs['tableFilterParams'];
const tableFilters = inject('$tableFilters') as TableRefs['tableFilters'];
const updateSearch = inject('$updateSearch') as TableRefs['updateSearch'];
const refreshItems = inject('$refreshItems') as TableRefs['refreshItems'];
const createItem = inject('$createItem') as TableRefs['createItem'];
const deleteItem = inject('$deleteItem') as TableRefs['deleteItem'];
const infoMessage = inject('$infoMessage') as TableRefs['infoMessage'];

const tableMenu = useTableMenu(path.value);

/** ... */
const hasFilterTags = computed(() => !!Object.keys(filterTags).length);
/** ... */
const hasFilterParams = computed(() => !!Object.keys(filterParams).length);
/** ... */
const hasSelection = computed(() => !!selection.value.length);

/** Table "search" filter text. */
const search = computed({
  get: () => queryText.value ?? '',
  set: updateSearch,
});

/** Table "additional options" menu items. */
const menuItems = computed(() => {
  if (!tableMenu.value.length) return null;

  const items: (MenuButton | MenuSeparator)[] = [];

  for (const [index, item] of tableMenu.value.entries()) {
    if ('type' in item) {
      items.push({ key: `item-${index}`, type: 'separator' });
    } else {
      items.push(createMenuItemButton(item));
    }
  }

  return items;
});

function createMenuItemButton(item: TablePanel.MenuItemButton<DataItem>) {
  let disabled;

  if (typeof item.disabled === 'boolean') {
    disabled = item.disabled;
  } else if (typeof item.disabled === 'function') {
    disabled = item.disabled({ selection: selection.value.map((o) => o.item) });
  } else {
    disabled = false;
  }

  const click = () => {
    void item.click({ selection: selection.value.map((o) => o.item) });
  };

  return { ...item, type: 'button', disabled, click } as MenuButton;
}

function clearSearch() {
  search.value = '';
}

function toggleFullscreenMode() {
  emit(fullscreen.value ? 'minimize' : 'maximize');
}

// Add a computed property to filter visible params
const visibleFilterParams = computed(() => {
  if (!filterParams) return {};

  return Object.entries(filterParams).reduce((acc, [key, param]) => {
    if (!param.hidden || !param.hidden(path.value.user)) {
      acc[key] = param;
    }
    return acc;
  }, {} as typeof filterParams);
});

const hasVisibleFilterParams = computed(() => 
  Object.keys(visibleFilterParams.value).length > 0
);

// Add a computed property to determine if we should show the filter params section
const showFilterParams = computed(() => {
  return Object.keys(visibleFilterParams.value || {}).length > 0;
});

return { __sfc: true,emit, path, tableLoading, createAction, deleteAction, queryText, selection, fullscreen, filterTags, filterParams, tableFilters, updateSearch, refreshItems, createItem, deleteItem, infoMessage, tableMenu, hasFilterTags, hasFilterParams, hasSelection, search, menuItems, createMenuItemButton, clearSearch, toggleFullscreenMode, visibleFilterParams, hasVisibleFilterParams, showFilterParams, faPlus, faTrashCan, faSync, faEllipsisV, faExpandAlt, faCompressAlt, faFilter, faInfo }
}

})